import React, { Fragment, useEffect, useState, useMemo } from "react";
import Breadcrumb from "../../common/breadcrumb";
import { ChevronDown, ChevronUp, Edit } from "react-feather";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
} from "reactstrap";
import {
  getSystemProductListing,
  getSystemProductCategoryListing,
  fetchSystemProducts,
  createSystemBundle,
} from "../../../utils/apiCalls";
import { FALLBACK_IMAGE } from "../../../utils/imageUtils";
import { Currency } from "../../../constants/currency";
import { useSelector } from "react-redux";

const SystemProductList = () => {
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const loggedInMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? loggedInMerchantId : reduxSelectedMerchant;
  const { merchantID } = useParams();

  const [isFetching, setIsFetching] = useState(false);
  const [productData, setProductData] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const [syncing, setSyncing] = useState({});

  // Filters State
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    categoryId: "",
    isActive: "true",
  });
  const [tempFilters, setTempFilters] = useState(filters);
  const [categories, setCategories] = useState([]);

  // Create Box modal — a box is our own product, configured on the edit page.
  const [boxModalOpen, setBoxModalOpen] = useState(false);
  const [boxForm, setBoxForm] = useState({ title: "", arabicName: "", price: "" });
  const [isCreatingBox, setIsCreatingBox] = useState(false);

  const createBox = async () => {
    setIsCreatingBox(true);
    const box = await createSystemBundle(selectedMerchant, {
      title: boxForm.title,
      arabicName: boxForm.arabicName,
      price: boxForm.price === "" ? 0 : parseFloat(boxForm.price),
    });
    setIsCreatingBox(false);
    if (box) {
      setBoxModalOpen(false);
      setBoxForm({ title: "", arabicName: "", price: "" });
      navigate(`/products/edit-system-product/${box.id}`);
    }
  };

  const handleExpandClick = (productId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(productId)) {
      newExpandedRows.delete(productId);
    } else {
      newExpandedRows.add(productId);
    }
    setExpandedRows(newExpandedRows);
  };

  const fetchProducts = async () => {
    if (!selectedMerchant || selectedMerchant === "all") return;
    setIsFetching(true);
    const data = await getSystemProductListing(selectedMerchant, filters);
    setProductData(data || []);
    setIsFetching(false);
  };

  useEffect(() => {
    fetchProducts();
  }, [selectedMerchant, filters]);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!selectedMerchant || selectedMerchant === "all") return;
      const cats = await getSystemProductCategoryListing(selectedMerchant);
      setCategories(cats || []);
    };
    fetchCategories();
  }, [selectedMerchant]);

  useEffect(() => {
    if (filterModalOpen) {
      setTempFilters(filters);
    }
  }, [filterModalOpen, filters]);

  const applyFilters = () => {
    setFilters(tempFilters);
    setFilterModalOpen(false);
  };

  const clearFilters = () => {
    const cleared = { search: "", categoryId: "", isActive: "true" };
    setTempFilters(cleared);
    setFilters(cleared);
    setFilterModalOpen(false);
  };

  const filteredProducts = useMemo(() => {
    let result = productData;

    if (filters.categoryId === "unassigned") {
      result = result.filter((p) => !p.SystemProductCategoryId);
    } else if (filters.categoryId) {
      result = result.filter((p) => p.SystemProductCategoryId === parseInt(filters.categoryId));
    }

    return result;
  }, [productData, filters.categoryId]);

  const renderAppliedFilters = () => {
    const activeFilters = Object.entries(filters).filter(([key, value]) => value);
    if (activeFilters.length === 0) return null;

    const getFilterPill = (key, value) => {
      let label = "";
      let displayValue = value;

      switch (key) {
        case "search":
          label = "Search";
          break;
        case "categoryId":
          label = "Category";
          if (value === "unassigned") {
            displayValue = "Unassigned";
          } else {
            const cat = categories.find((c) => c.id == value);
            displayValue = cat ? cat.name : value;
          }
          break;
        case "isActive":
          label = "Status";
          displayValue = value === "true" ? "Active" : "Inactive";
          break;
        default:
          return null;
      }

      return (
        <Badge
          color="dark"
          className="me-2 mb-2 p-2"
          key={key}
          style={{ backgroundColor: "#009689", fontSize: "0.8rem" }}
        >
          <strong>{label}:</strong> {displayValue}
        </Badge>
      );
    };

    return (
      <div className="mb-3">
        {activeFilters.map(([key, value]) => getFilterPill(key, value))}
      </div>
    );
  };

  const getProductMainImage = (product) => {
    const images = product?.SystemProductImages || [];
    const mainImg = images.find((img) => img.is_main);
    const firstImg = images[0];
    const img = mainImg || firstImg;
    if (img) return process.env.REACT_APP_URL + img.image;
    return FALLBACK_IMAGE;
  };

  const runSystemProductsSync = async () => {
    setSyncing((s) => ({ ...s, ['systemProducts']: true }));
    try {
        await fetchSystemProducts(loggedInMerchantId);
      } finally {
        setSyncing((s) => ({ ...s, ['systemProducts']: false }));
      }
    await fetchSystemProducts(loggedInMerchantId)
  };

  return (
    <Fragment>
      <Breadcrumb title="System Products" parent="Products" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader className="d-flex justify-content-between">
                <h5>System Products</h5>
                <div className="d-flex gap-2">
                  <Button
                    color="primary"
                    onClick={() => setBoxModalOpen(true)}
                    disabled={!selectedMerchant || selectedMerchant === "all"}
                  >
                    Create Box
                  </Button>
                  <Button color="primary" onClick={() => runSystemProductsSync()} disabled={syncing.systemProducts}
                          loading={syncing.systemProducts} >
                    Fetch System Products
                  </Button>
                  <Button color="primary" onClick={() => setFilterModalOpen(true)}>
                    Filters
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {renderAppliedFilters()}
                {isFetching ? (
                  <div className="text-center py-4">Loading...</div>
                ) : !selectedMerchant || selectedMerchant === "all" ? (
                  <div className="text-center text-muted py-4">
                    Please select a merchant to view system products.
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    {productData.length === 0
                      ? "No system products found. Use 'Fetch System Products' from the merchant edit page to sync."
                      : "No products match your filters."}
                  </div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ borderBottom: "2px solid #dee2e6" }}>
                      <tr>
                        <th style={tableHeaderStyle}>ID</th>
                        <th style={tableHeaderStyle}>Image</th>
                        <th style={tableHeaderStyle}>Name</th>
                        <th style={tableHeaderStyle}>Price</th>
                        <th style={tableHeaderStyle}>Category</th>
                        <th style={tableHeaderStyle}>Status</th>
                        <th style={tableHeaderStyle}>Best Seller</th>
                        <th style={tableHeaderStyle}>Actions</th>
                        <th style={tableHeaderStyle}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((myData) => (
                        <Fragment key={myData.id}>
                          <tr
                            style={{
                              borderBottom: "1px solid #dee2e6",
                              verticalAlign: "middle",
                              cursor: "pointer",
                            }}
                            onClick={() => handleExpandClick(myData.id)}
                          >
                            <td style={tableCellStyle}>{myData?.id}</td>
                            <td style={tableCellStyle}>
                              <img
                                alt=""
                                src={getProductMainImage(myData)}
                                onError={(e) => {
                                  e.target.src = FALLBACK_IMAGE;
                                }}
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  borderRadius: "5px",
                                  objectFit: "cover",
                                }}
                              />
                            </td>
                            <td style={tableCellStyle}>
                              {myData?.title}
                              {myData?.productClass === "bundle" && (
                                <Badge color="warning" className="ms-2">Box</Badge>
                              )}
                            </td>
                            <td style={tableCellStyle}>
                              {myData?.price} {Currency.sr}
                            </td>
                            <td style={tableCellStyle}>
                              {myData?.SystemProductCategory ? (
                                <Badge color="info">
                                  {myData.SystemProductCategory.name}
                                </Badge>
                              ) : (
                                <Badge color="secondary">Unassigned</Badge>
                              )}
                            </td>
                            <td style={tableCellStyle}>
                              {myData.is_active ? (
                                <span style={{ color: "#008000" }}>Active</span>
                              ) : (
                                <span style={{ color: "#ff0000" }}>Inactive</span>
                              )}
                            </td>
                            <td style={tableCellStyle}>
                              {myData.bestSeller ? (
                                <span style={{ color: "#008000" }}>Active</span>
                              ) : (
                                <span style={{ color: "#ff0000" }}>Inactive</span>
                              )}
                            </td>
                            <td style={tableCellStyle}>
                              <Button
                                color="primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/products/edit-system-product/${myData.id}`);
                                }}
                              >
                                <Edit size={14} />
                              </Button>
                            </td>
                            <td style={tableCellStyle}>
                              <div
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "50%",
                                  backgroundColor: "#fff",
                                  border: "1px solid #e6e6e6",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {expandedRows.has(myData.id) ? (
                                  <ChevronUp size={16} />
                                ) : (
                                  <ChevronDown size={16} />
                                )}
                              </div>
                            </td>
                          </tr>
                          {expandedRows.has(myData.id) && (
                            <tr className="expandable-row">
                              <td colSpan="8" style={{ padding: "0" }}>
                                <div
                                  style={{
                                    padding: "20px",
                                    backgroundColor: "#f8f9fa",
                                    borderBottom: "1px solid #dee2e6",
                                  }}
                                >
                                  <Row>
                                    <Col md="12">
                                      <h6
                                        className="mb-3"
                                        style={{
                                          color: "#009689",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        Product Details
                                      </h6>
                                      <Row>
                                        <Col sm="4" className="mb-3">
                                          <div className="text-muted small">
                                            Arabic Name
                                          </div>
                                          <div className="fw-bold">
                                            {myData.arabicName || "N/A"}
                                          </div>
                                        </Col>
                                        <Col sm="4" className="mb-3">
                                          <div className="text-muted small">
                                            External Product ID
                                          </div>
                                          <div className="fw-bold">
                                            {myData.merchant_product_id || "N/A"}
                                          </div>
                                        </Col>
                                        <Col sm="4" className="mb-3">
                                          <div className="text-muted small">
                                            Sort Order
                                          </div>
                                          <div className="fw-bold">
                                            {myData.sortOrder || 0}
                                          </div>
                                        </Col>
                                      </Row>
                                      {myData.description && (
                                        <Row>
                                          <Col sm="12" className="mb-3">
                                            <div className="text-muted small mb-1">Description</div>
                                            <div
                                              className="border rounded p-2 bg-white"
                                              dangerouslySetInnerHTML={{ __html: myData.description }}
                                            />
                                          </Col>
                                        </Row>
                                      )}
                                      {(myData.SystemProductImages?.length > 0) && (
                                        <Row>
                                          <Col sm="12" className="mb-3">
                                            <div className="text-muted small mb-1">Images</div>
                                            <div className="d-flex flex-wrap gap-2">
                                              {myData.SystemProductImages.map((img) => (
                                                <div key={img.id} style={{ position: "relative" }}>
                                                  <img
                                                    src={process.env.REACT_APP_URL + img.image}
                                                    alt=""
                                                    onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                                                    style={{
                                                      width: "80px",
                                                      height: "80px",
                                                      objectFit: "cover",
                                                      borderRadius: "5px",
                                                      border: img.is_main ? "3px solid #009689" : "1px solid #dee2e6",
                                                    }}
                                                  />
                                                  {img.is_main && (
                                                    <Badge
                                                      color="danger"
                                                      style={{
                                                        position: "absolute",
                                                        top: "-5px",
                                                        right: "-5px",
                                                        fontSize: "0.6rem",
                                                      }}
                                                    >
                                                      Main
                                                    </Badge>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </Col>
                                        </Row>
                                      )}
                                    </Col>
                                  </Row>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Create Box Modal */}
      <Modal isOpen={boxModalOpen} toggle={() => setBoxModalOpen(false)}>
        <ModalHeader toggle={() => setBoxModalOpen(false)}>Create Box</ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            A box is sold on the website but does not exist in Odoo. Its price is set here; the
            products inside it are sent to Odoo as separate lines. The box starts inactive — add
            its slots on the next page, then activate it.
          </p>
          <Form>
            <FormGroup>
              <Label>Title</Label>
              <Input
                type="text"
                placeholder="e.g. Box of 5"
                value={boxForm.title}
                onChange={(e) => setBoxForm({ ...boxForm, title: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Arabic Name</Label>
              <Input
                type="text"
                dir="rtl"
                value={boxForm.arabicName}
                onChange={(e) => setBoxForm({ ...boxForm, arabicName: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Box Price ({Currency.sr})</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={boxForm.price}
                onChange={(e) => setBoxForm({ ...boxForm, price: e.target.value })}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={createBox} disabled={isCreatingBox || !boxForm.title.trim()}>
            {isCreatingBox ? "Creating..." : "Create and configure"}
          </Button>
          <Button color="secondary" onClick={() => setBoxModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Filter Modal */}
      <Modal isOpen={filterModalOpen} toggle={() => setFilterModalOpen(false)}>
        <ModalHeader toggle={() => setFilterModalOpen(false)}>
          Filter System Products
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Search</Label>
              <Input
                type="text"
                placeholder="Search by name"
                value={tempFilters.search}
                onChange={(e) =>
                  setTempFilters({ ...tempFilters, search: e.target.value })
                }
              />
            </FormGroup>
            <FormGroup>
              <Label>Category</Label>
              <Input
                type="select"
                value={tempFilters.categoryId}
                onChange={(e) =>
                  setTempFilters({ ...tempFilters, categoryId: e.target.value })
                }
              >
                <option value="">All Categories</option>
                <option value="unassigned">Unassigned</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Status</Label>
              <Input
                type="select"
                value={tempFilters.isActive}
                onChange={(e) =>
                  setTempFilters({ ...tempFilters, isActive: e.target.value })
                }
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Input>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={applyFilters}>
            Apply
          </Button>
          <Button color="secondary" onClick={clearFilters}>
            Clear
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

const tableHeaderStyle = {
  padding: "10px",
  textAlign: "left",
  background: "#f2f2f2",
  borderBottom: "1px solid #dee2e6",
};

const tableCellStyle = {
  padding: "10px",
  textAlign: "left",
};

export default SystemProductList;
